import type { History, Location } from "history";

// Maximum URL size before switching to sessionStorage (bytes)
// Conservative limit to avoid Chrome crashes
const URL_SIZE_THRESHOLD = 1500;

// SessionStorage key prefix
const STORAGE_PREFIX = "filter_state_";

// URL param for filter hash
const HASH_PARAM = "_fs";

// TTL for sessionStorage entries (24 hours)
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

export type Filters = { [key: string]: string[] };

interface StoredFilterState {
  filters: Filters;
  timestamp: number;
}

/**
 * Generate a stable hash from filter object for sessionStorage key
 */
function generateFilterHash(filters: Filters): string {
  const str = JSON.stringify(filters);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Calculate byte size of filters when encoded in URL
 */
function getFilterSize(filters: Filters): number {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, values]) => {
    values.forEach((value) => {
      params.append(key, value);
    });
  });
  return new TextEncoder().encode(params.toString()).length;
}

/**
 * Parse URL params into filter object, grouping duplicate keys into arrays
 */
export function groupPortfolioParamsByKey(params: URLSearchParams): Filters {
  return [...params.entries()].reduce<Filters>((acc, tuple) => {
    const [key, val] = tuple;
    if (key in acc) {
      acc[key] = [...acc[key], val];
    } else {
      // Skip 'tab' key as it's not a filter
      if (key !== "tab") {
        acc[key] = [val];
      }
    }
    return acc;
  }, {});
}

/**
 * Clean up old sessionStorage filter entries (older than TTL)
 */
function cleanupOldFilters(): void {
  const now = Date.now();
  const keysToRemove: string[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const state: StoredFilterState = JSON.parse(stored);
          if (now - state.timestamp > STORAGE_TTL_MS) {
            keysToRemove.push(key);
          }
        }
      } catch (e) {
        // Invalid JSON, remove it
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Load filters from URL or sessionStorage
 */
export function loadFilters(location: Location): Filters {
  const params = new URLSearchParams(location.search.substring(1));

  // Check if filters are in sessionStorage
  const hashParam = params.get(HASH_PARAM);
  if (hashParam) {
    const storageKey = STORAGE_PREFIX + hashParam;
    const stored = sessionStorage.getItem(storageKey);

    if (stored) {
      try {
        const state: StoredFilterState = JSON.parse(stored);

        // Check if expired
        if (Date.now() - state.timestamp <= STORAGE_TTL_MS) {
          return state.filters;
        } else {
          // Expired, clean up
          sessionStorage.removeItem(storageKey);
        }
      } catch (e) {
        // Invalid JSON, fall through to URL parsing
        sessionStorage.removeItem(storageKey);
      }
    }

    // If we get here, hash was in URL but storage was empty/invalid
    // Fall through to parse any remaining URL params
  }

  // Parse filters from URL params
  return groupPortfolioParamsByKey(params);
}

/**
 * Save filters to URL or sessionStorage based on size
 * Returns true if using sessionStorage, false if using URL, null if no actual filters
 */
export function saveFilters(
  filters: Filters,
  history: History,
  pathname: string,
  preserveParams?: { q?: string; offset?: string; perPage?: string }
): boolean | null {
  // Clean up old entries periodically
  if (Math.random() < 0.1) {
    // 10% chance on each save
    cleanupOldFilters();
  }

  // Check if there are any actual filter values (excluding metadata)
  const hasActualFilters = Object.entries(filters).some(
    ([key, values]) =>
      key !== "active_tag_view" &&
      key !== "q" &&
      key !== "offset" &&
      key !== "perPage" &&
      values &&
      values.length > 0
  );

  // If no actual filters, clear the URL
  if (!hasActualFilters) {
    const params = new URLSearchParams();

    // Preserve non-filter params
    if (preserveParams?.q) params.set("q", preserveParams.q);
    if (preserveParams?.offset) params.set("offset", preserveParams.offset);
    if (preserveParams?.perPage) params.set("perPage", preserveParams.perPage);

    const search = params.toString().replace(/\+/g, "%20");
    history.push({
      pathname,
      search: search ? `?${search}` : "",
    });

    return null;
  }

  const filterSize = getFilterSize(filters);
  const useSessionStorage = filterSize > URL_SIZE_THRESHOLD;

  const params = new URLSearchParams();

  // Preserve non-filter params
  if (preserveParams?.q) params.set("q", preserveParams.q);
  if (preserveParams?.offset) params.set("offset", preserveParams.offset);
  if (preserveParams?.perPage) params.set("perPage", preserveParams.perPage);

  if (useSessionStorage) {
    // Store filters in sessionStorage
    const hash = generateFilterHash(filters);
    const storageKey = STORAGE_PREFIX + hash;

    const state: StoredFilterState = {
      filters,
      timestamp: Date.now(),
    };

    sessionStorage.setItem(storageKey, JSON.stringify(state));
    params.set(HASH_PARAM, hash);
  } else {
    // Store filters in URL
    Object.entries(filters).forEach(([key, values]) => {
      values.forEach((value) => {
        params.append(key, value);
      });
    });
  }

  const search = params.toString().replace(/\+/g, "%20");
  history.push({
    pathname,
    search: search ? `?${search}` : "",
  });

  return useSessionStorage;
}

/**
 * Check if filters would fit in URL without exceeding threshold
 */
export function wouldFitInURL(filters: Filters): boolean {
  return getFilterSize(filters) <= URL_SIZE_THRESHOLD;
}

/**
 * Get current storage method for active filters
 */
export function getStorageMethod(
  location: Location
): "url" | "sessionStorage" | null {
  const params = new URLSearchParams(location.search.substring(1));
  const hashParam = params.get(HASH_PARAM);

  if (hashParam) {
    const storageKey = STORAGE_PREFIX + hashParam;
    if (sessionStorage.getItem(storageKey)) {
      return "sessionStorage";
    }
  }

  // Check if there are any filter params (excluding special params)
  const hasFilters = Array.from(params.keys()).some(
    (key) =>
      key !== "q" &&
      key !== "offset" &&
      key !== "perPage" &&
      key !== "tab" &&
      key !== HASH_PARAM
  );

  return hasFilters ? "url" : null;
}

/**
 * Clear all filter state (URL and sessionStorage)
 */
export function clearFilters(
  history: History,
  pathname: string,
  preserveParams?: { q?: string; offset?: string; perPage?: string }
): void {
  const params = new URLSearchParams();

  // Preserve non-filter params
  if (preserveParams?.q) params.set("q", preserveParams.q);
  if (preserveParams?.offset) params.set("offset", preserveParams.offset);
  if (preserveParams?.perPage) params.set("perPage", preserveParams.perPage);

  const search = params.toString().replace(/\+/g, "%20");
  history.replace({
    pathname,
    search: search ? `?${search}` : "",
  });
}

/**
 * Convert filters to new API format with wildcard and exclude support
 * @param filters - Current filter state (old format)
 * @param availableFilters - Metadata about all available filter options
 * @returns New API format with filters and optional exclude_filters
 */
export function convertToWildcardFormat(
  filters: Filters,
  availableFilters: {
    [filterType: string]: { name: string; id?: string | null }[];
  }
): {
  filters: Record<string, string[] | "*">;
  exclude_filters?: Record<string, string[]>;
} {
  const newFilters: Record<string, string[] | "*"> = {};
  const excludeFilters: Record<string, string[]> = {};

  Object.entries(filters).forEach(([filterType, selectedValues]) => {
    // Skip non-filter params
    if (
      filterType === "q" ||
      filterType === "limit" ||
      filterType === "offset" ||
      filterType === "perPage" ||
      filterType === "active_tag_view"
    ) {
      return;
    }

    const available = availableFilters[filterType];

    // Always omit empty arrays, regardless of metadata availability
    if (Array.isArray(selectedValues) && selectedValues.length === 0) {
      return;
    }

    if (!available || !Array.isArray(selectedValues)) {
      // No metadata or not an array - pass through as-is
      newFilters[filterType] = selectedValues;
      return;
    }

    const selectedCount = selectedValues.length;
    const totalCount = available.length;

    if (selectedCount === totalCount) {
      // All items selected - use wildcard
      newFilters[filterType] = "*";
    } else if (selectedCount > totalCount * 0.5) {
      // More than 50% selected - use exclude mode
      const selectedSet = new Set(selectedValues);
      const excluded = available
        .map((item) => item.name)
        .filter((name) => !selectedSet.has(name));

      if (excluded.length > 0) {
        newFilters[filterType] = "*";
        excludeFilters[filterType] = excluded;
      } else {
        // Fallback to normal mode if exclusion logic fails
        newFilters[filterType] = selectedValues;
      }
    } else {
      // Less than 50% selected - use normal include mode
      newFilters[filterType] = selectedValues;
    }
  });

  const result: {
    filters: Record<string, string[] | "*">;
    exclude_filters?: Record<string, string[]>;
  } = {
    filters: newFilters,
  };

  if (Object.keys(excludeFilters).length > 0) {
    result.exclude_filters = excludeFilters;
  }

  return result;
}
